import { safeMedicalCertificateFileName as safeStorageName } from "../../../utils/medicalCertificateFileName";
import AnnualMaskDoctorEmailStatus from "./AnnualMaskDoctorEmailStatus";
import { isAnnualMaskSale, loadAnnualMaskAllowedNodes, loadAnnualMaskSaleCaseDocs, isAnnualMaskCaseInNodes } from "./annualMaskSaleNodeAccess";
import AnnualMaskPhysicianCase from "./AnnualMaskPhysicianCase";
import AnnualMaskTestEmailButton from "./AnnualMaskTestEmailButton";
import { queueAnnualMaskEmail } from "./queueAnnualMaskEmail";
import { canUploadAnnualMaskCertificate, getAnnualMaskPhysicianCertificateState, isAnnualMaskCertificateUploadLocked, saveAnnualMaskSignedCertificate } from "./annualMaskPhysicianUpload";
import { isAnnualMaskPhysician, loadAnnualMaskCaseDocs } from "./annualMaskPhysicianQuery";
import { getAnnualMaskDoctorSkipUnavailableReason } from "./annualMaskPurchaseUtils";
import AnnualMaskWorkflowProgress from "./AnnualMaskWorkflowProgress";
import AnnualMaskCompleteButton from "./AnnualMaskCompleteButton";
import AnnualMaskReceiptDate, { AnnualMaskReceiptDateText } from "./AnnualMaskReceiptDate";
import { isAnnualMaskReceiptDateValid, withAnnualMaskDefaultSale } from "./annualMaskPurchaseUtils";
import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Card, Form, Modal } from "react-bootstrap";
import swal from "sweetalert";
import "./AnnualMaskPurchase.css";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import { db, storage } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import PageTitle from "../../layouts/PageTitle";
import Toast, {
  notifyError,
  notifyTopRight,
  notifyWarning,
} from "../toast/SetToast";
import {
  canGenerateAnnualMaskMedicalCert,
  generateAnnualMaskMedicalCertBlob,
  getAnnualMaskMedicalCertFileName,
} from "../Export/ExportTab/annualMaskMedicalCert";
import {
  ANNUAL_MASK_ACCESS_COLLECTION,
  ANNUAL_MASK_BATCHES_COLLECTION,
  ANNUAL_MASK_CASES_COLLECTION,
  ANNUAL_MASK_SOCIAL_SECURITY_DECISION,
  ANNUAL_MASK_STATUS,
  canSkipAnnualMaskDoctorStep,
  canSkipAnnualMaskSignedCertificate,
  isAnnualMaskSignedCertificateStepComplete,
  advanceAnnualMaskStatus,
  canAdministerAnnualMaskPurchase,
  canManageAnnualMaskPurchase,
  getAnnualMaskCaseStatusForSaleOrder,
  getAnnualMaskCasePath,
  getAnnualMaskSelectedSaleId,
  getAnnualMaskSelectedSaleName,
  getAnnualMaskSocialSecurityDecision,
  getAnnualMaskStatusConfig,
  isAnnualMaskSuperAdmin,
  normalizeAnnualMaskAccessEmail,
} from "./annualMaskPurchaseUtils";
import { normalizeSleepStudyReportDocs } from "./cpapFollowupFileUtils";
import {
  getAnnualMaskSleepStudyReportReference,
  isAnnualMaskPdfFile,
  sortAnnualMaskFilesByNewest,
} from "./annualMaskClinicalDataUtils";
import { readAnnualMaskSignedPressure } from "./annualMaskClinicalPdfReader";
import { downloadAnnualMaskDoctorPackage } from "./annualMaskDoctorPackage";
import {
  createAnnualMaskSaleOrderWithStock,
  getAnnualMaskOrderPatientLabel,
} from "./annualMaskSaleOrder";

const asDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTime = (value) => {
  const date = asDate(value);
  if (!date) return "-";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const safeText = (value) =>
  value === undefined || value === null ? "" : String(value).trim();

const getCaseNode = (caseItem = {}) => {
  const patient = caseItem.patient || {};
  return safeText(caseItem.nodeRef || patient.nodeRef || caseItem.node || patient.node);
};

const getCaseCustomer = (caseItem = {}) => {
  const patient = caseItem.patient || {};
  return safeText(caseItem.refCustomer || patient.refCustomer || caseItem.customer || patient.customer);
};

const patientName = (patient = {}) =>
  [patient.firstnameTh || patient.firstname, patient.lastnameTh || patient.lastname]
    .map(safeText)
    .filter(Boolean)
    .join(" ");

const getFileType = (fileName = "") => {
  const extension = String(fileName).split(".").pop().toLocaleLowerCase();
  return ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(extension)
    ? "image"
    : "document";
};


const isPdf = (file) =>
  Boolean(
    file &&
      (file.type === "application/pdf" || /\.pdf$/i.test(file.name || "")),
  );

const getCaseFileUrl = async (fileData = {}) => {
  if (fileData.storagePath) {
    return getDownloadURL(ref(storage, fileData.storagePath));
  }
  if (/^https?:\/\//i.test(safeText(fileData.url))) return fileData.url;
  return "";
};

const getCaseFileMailAttachment = async (
  fileData = {},
  label = "File",
  fallbackFileName = "attachment.pdf",
  fallbackContentType = "application/pdf",
) => {
  const path = await getCaseFileUrl(fileData);
  if (!path) throw new Error(`${label} is unavailable.`);

  return {
    filename: fileData.fileName || fileData.savedFileName || fallbackFileName,
    path,
    contentType: fileData.contentType || fallbackContentType,
  };
};

const readCaseFileBlob = async (fileData = {}, label = "File") => {
  const url = await getCaseFileUrl(fileData);
  if (!url) throw new Error(`${label} is unavailable.`);

  const response = await fetch(url);
  if (!response.ok) throw new Error(`Unable to read ${label}.`);

  return response.blob();
};

const resolveCaseSleepStudyReport = async (caseItem = {}) => {
  const savedReport = caseItem.sleepStudyReport;
  if (savedReport && (savedReport.storagePath || savedReport.url)) {
    return getAnnualMaskSleepStudyReportReference(savedReport);
  }

  const hn = safeText(caseItem.hn);
  if (!hn) return null;

  const reportSnapshot = await getDocs(
    collection(db, "patients", hn, "report"),
  );
  const latestReport = sortAnnualMaskFilesByNewest(
    normalizeSleepStudyReportDocs(reportSnapshot.docs, hn).filter(
      isAnnualMaskPdfFile,
    ),
  )[0];

  return latestReport
    ? getAnnualMaskSleepStudyReportReference(latestReport)
    : null;
};

const isMaskAccessory = (item = {}) => {
  const category = safeText(item.category || item.productType).toLocaleLowerCase();
  const itemName = safeText(item.itemName).toLocaleLowerCase();
  return category === "mask" || itemName.includes("mask") || itemName.includes("หน้ากาก");
};

const formatLots = (lots = []) =>
  lots
    .filter((lot) => Number(lot.stockQuantity) > 0)
    .map((lot) => `${lot.lotNo || lot.id}: ${lot.stockQuantity}`)
    .join(", ") || "No available lot";

const getCurrentUserName = (currentUser) =>
  safeText(currentUser && (currentUser.displayName || currentUser.email));

const generateCaseMedicalCertificate = (caseItem = {}) => {
  const certificateData = caseItem.certificateData || {};
  return generateAnnualMaskMedicalCertBlob({
    patient: caseItem.patient || caseItem,
    nodeRef: caseItem.nodeRef || caseItem.node,
    doctor: caseItem.doctorName,
    ahi: certificateData.ahi,
    minSpo2: certificateData.minSpo2,
    minPressure: certificateData.minPressure,
    maxPressure: certificateData.maxPressure,
    pressure: certificateData.pressure,
    latestSleepStudyDate: certificateData.latestSleepStudyDate,
  });
};

const getBatchSortTime = (batch) => {
  const updated = asDate(batch.updatedAt);
  const created = asDate(batch.createdAt);
  return (updated || created || new Date(0)).getTime();
};

const isAnnualMaskPurchaseResultLocked = (caseItem = {}) =>
  [
    ANNUAL_MASK_STATUS.READY_TO_PURCHASE,
    ANNUAL_MASK_STATUS.ORDER_CREATED,
    ANNUAL_MASK_STATUS.ORDER_COMPLETED,
  ].includes(caseItem.status) || Boolean(caseItem.saleOrderId);

const AnnualMaskPurchase = () => {
  const { currentUser } = useAuth();
  const physicianOnly = isAnnualMaskPhysician(currentUser);
  const defaultStatusFilter = physicianOnly ? "Awaiting upload" : "All Status";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [batches, setBatches] = useState([]);
  const [masks, setMasks] = useState([]);
  const [sales, setSales] = useState([]);
  const [search, setSearch] = useState("");
  const [batchPage, setBatchPage] = useState(1);
  const [batchesPerPage, setBatchesPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState(defaultStatusFilter);
  const [nodeFilter, setNodeFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [filePreview, setFilePreview] = useState(null);
  const [hasAnnualMaskAccess, setHasAnnualMaskAccess] = useState(false);
  const [loadedScope, setLoadedScope] = useState("");
  const loadSequence = useRef(0);
  const saleNodeOnly = isAnnualMaskSale(currentUser);
  const scopeKey = JSON.stringify([
    currentUser && currentUser.uid,
    currentUser && currentUser.displayName,
    currentUser && currentUser.email,
    physicianOnly,
    saleNodeOnly,
  ]);
  const activeScope = useRef(scopeKey);
  activeScope.current = scopeKey;
  const signedCertificateRef = useRef(null);
  const pendingSignedCaseRef = useRef(null);

  const canManage = canManageAnnualMaskPurchase(currentUser);
  const canAdminister = canAdministerAnnualMaskPurchase(currentUser);
  const isSuperAdmin = isAnnualMaskSuperAdmin(currentUser);
  const currentEmail = normalizeAnnualMaskAccessEmail(
    currentUser && currentUser.email,
  );

  const loadData = async () => {
    const sequence = ++loadSequence.current;
    const isCurrentLoad = () => sequence === loadSequence.current && activeScope.current === scopeKey;
    setLoading(true);
    if (!currentUser) {
      setBatches([]);
      setLoadedScope(scopeKey);
      setLoading(false);
      return;
    }
    try {
      const allowedNodes = saleNodeOnly ? await loadAnnualMaskAllowedNodes(db, currentUser) : [];
      const [batchSnapshot, accessorySnapshot, salesSnapshot] =
        await Promise.all([
          getDocs(collection(db, ANNUAL_MASK_BATCHES_COLLECTION)),
          getDocs(collection(db, "accessories")),
          getDocs(
            query(
              collection(db, "sales"),
              where("status", "==", "Available"),
            ),
          ),
        ]);

      const accessoryRows = accessorySnapshot.docs
        .map((accessoryDoc) => ({ id: accessoryDoc.id, ...accessoryDoc.data() }))
        .filter(isMaskAccessory);
      const nextMasks = await Promise.all(
        accessoryRows.map(async (mask) => {
          try {
            const lotSnapshot = await getDocs(
              collection(db, "accessories", mask.id, "lots"),
            );
            return {
              ...mask,
              lots: lotSnapshot.docs.map((lotDoc) => ({
                id: lotDoc.id,
                ...lotDoc.data(),
              })),
            };
          } catch (error) {
            console.warn("Unable to load mask lots", error);
            return { ...mask, lots: [] };
          }
        }),
      );

      const nextBatches = await Promise.all(
        batchSnapshot.docs.map(async (batchDoc) => {
          const casesRef = collection(db, ANNUAL_MASK_BATCHES_COLLECTION, batchDoc.id, ANNUAL_MASK_CASES_COLLECTION);
          const caseDocs = saleNodeOnly && !physicianOnly
            ? await loadAnnualMaskSaleCaseDocs(casesRef, allowedNodes)
            : await loadAnnualMaskCaseDocs(casesRef, currentUser);
          const cases = caseDocs
            .filter((row) => !saleNodeOnly || isAnnualMaskCaseInNodes(row.data(), allowedNodes))
            .map((caseDoc) => {
              const caseData = caseDoc.data();
              return {
                id: caseDoc.id,
                batchId: batchDoc.id,
                refPath: getAnnualMaskCasePath(batchDoc.id, caseDoc.id),
                ...caseData,
                batchName: safeText(
                  batchDoc.data().batchName || caseData.batchName,
                ),
              };
            })
            .sort((left, right) =>
              String(right.createdAt || "").localeCompare(
                String(left.createdAt || ""),
              ),
            );

          return {
            id: batchDoc.id,
            ...batchDoc.data(),
            cases,
          };
        }),
      );

      if (!isCurrentLoad()) return;
      setMasks(
        nextMasks.sort((left, right) =>
          safeText(left.itemName).localeCompare(safeText(right.itemName)),
        ),
      );
      setSales(
        salesSnapshot.docs
          .map((saleDoc) => ({ id: saleDoc.id, ...saleDoc.data() }))
          .filter((sale) => safeText(sale.name))
          .sort((left, right) =>
            safeText(left.name).localeCompare(safeText(right.name)),
          ),
      );
      setLoadedScope(scopeKey);
      setBatches(nextBatches.filter((batch) => (!physicianOnly && !saleNodeOnly) || batch.cases.length > 0).sort((left, right) => getBatchSortTime(right) - getBatchSortTime(left)));
    } catch (error) {
      if (!isCurrentLoad()) return;
      setBatches([]);
      setLoadedScope(scopeKey);
      console.error("Unable to load annual mask purchases", error);
      notifyError("Cannot load Annual Mask Purchase");
    } finally {
      if (isCurrentLoad()) setLoading(false);
    }
  };

  useEffect(() => {
    setFilePreview(null);
    pendingSignedCaseRef.current = null;
    setStatusFilter(defaultStatusFilter);
    setNodeFilter("");
    setCustomerFilter("");
    loadData();
    return () => { loadSequence.current += 1; };
  }, [scopeKey]);

  useEffect(() => {
    let active = true;

    const loadOwnAccess = async () => {
      if (isSuperAdmin) {
        if (active) setHasAnnualMaskAccess(true);
        return;
      }
      if (!currentEmail) {
        if (active) setHasAnnualMaskAccess(false);
        return;
      }

      try {
        const accessDoc = await getDoc(
          doc(db, ANNUAL_MASK_ACCESS_COLLECTION, currentEmail),
        );
        if (active) setHasAnnualMaskAccess(accessDoc.exists());
      } catch (error) {
        console.warn("Unable to read Annual Mask Access", error);
        if (active) setHasAnnualMaskAccess(false);
      }
    };

    loadOwnAccess();
    return () => {
      active = false;
    };
  }, [currentEmail, isSuperAdmin]);

  const { nodeOptions, customerOptions } = useMemo(() => {
    const nodes = new Set();
    const customers = new Set();
    if (isSuperAdmin && loadedScope === scopeKey) {
      batches.forEach((batch) => (batch.cases || []).forEach((caseItem) => {
        const node = getCaseNode(caseItem);
        const customer = getCaseCustomer(caseItem);
        if (node) nodes.add(node);
        if (customer && (!nodeFilter || node === nodeFilter)) customers.add(customer);
      }));
    }
    const sortOptions = (values) => Array.from(values).sort((left, right) => left.localeCompare(right));
    return { nodeOptions: sortOptions(nodes), customerOptions: sortOptions(customers) };
  }, [batches, nodeFilter, isSuperAdmin, loadedScope, scopeKey]);

  const filteredBatches = useMemo(() => {
    if (loadedScope !== scopeKey) return [];
    const normalizedSearch = search.trim().toLocaleLowerCase();

    return batches
      .map((batch) => {
        const visibleCases = (batch.cases || []).filter((caseItem) => {
          if (isSuperAdmin && nodeFilter && getCaseNode(caseItem) !== nodeFilter) return false;
          if (isSuperAdmin && customerFilter && getCaseCustomer(caseItem) !== customerFilter) return false;
          const filterStatus = physicianOnly ? getAnnualMaskPhysicianCertificateState(caseItem) : caseItem.status;
          if (statusFilter !== "All Status" && filterStatus !== statusFilter) {
            return false;
          }

          if (!normalizedSearch) return true;
          return [
            batch.batchName,
            caseItem.hn,
            patientName(caseItem.patient || caseItem),
            ...(!physicianOnly ? [caseItem.doctorName, getAnnualMaskSelectedSaleName(caseItem), caseItem.selectedMask && caseItem.selectedMask.itemName, caseItem.saleOrderReceiptNo] : []),
          ]
            .filter(Boolean)
            .join(" ")
            .toLocaleLowerCase()
            .includes(normalizedSearch);
        });

        return { ...batch, cases: visibleCases };
      })
      .filter((batch) => batch.cases.length > 0);
  }, [batches, search, statusFilter, nodeFilter, customerFilter, isSuperAdmin, loadedScope, scopeKey, physicianOnly]);

  const batchPageCount = Math.max(1, Math.ceil(filteredBatches.length / batchesPerPage));
  const currentBatchPage = Math.min(batchPage, batchPageCount);
  const batchPageStart = (currentBatchPage - 1) * batchesPerPage;
  const visibleBatches = filteredBatches.slice(batchPageStart, batchPageStart + batchesPerPage);

  useEffect(() => {
    setBatchPage(1);
  }, [search, statusFilter, nodeFilter, customerFilter, batchesPerPage, scopeKey]);

  useEffect(() => {
    if (!loading) setBatchPage((page) => Math.min(page, batchPageCount));
  }, [loading, batchPageCount]);

  const renderBatchPagination = () => (
    <nav className="annual-mask-pagination" aria-label="Annual Mask batch pagination">
      <span className="small text-muted" aria-live="polite">
        {filteredBatches.length ? `${batchPageStart + 1}–${Math.min(batchPageStart + batchesPerPage, filteredBatches.length)}` : "0"} of {filteredBatches.length} batch(es)
        {` · ${filteredBatches.reduce((total, batch) => total + batch.cases.length, 0)} matching case(s)`}
      </span>
      <div className="d-flex flex-wrap align-items-center annual-mask-page-controls">
        <label className="mb-0 d-flex align-items-center">
          <span className="small mr-2">Batches per page</span>
          <select className="form-control form-control-sm w-auto" value={batchesPerPage} onChange={(event) => setBatchesPerPage(Number(event.target.value))}>
            {[5, 10, 20, 50].map((size) => <option key={size} value={size}>{size}</option>)}
          </select>
        </label>
        <Button size="sm" variant="outline-secondary" disabled={currentBatchPage === 1} onClick={() => setBatchPage(1)}>First</Button>
        <Button size="sm" variant="outline-secondary" disabled={currentBatchPage === 1} onClick={() => setBatchPage(currentBatchPage - 1)}>Previous</Button>
        <span className="small">Page {currentBatchPage} of {batchPageCount}</span>
        <Button size="sm" variant="outline-secondary" disabled={currentBatchPage === batchPageCount} onClick={() => setBatchPage(currentBatchPage + 1)}>Next</Button>
        <Button size="sm" variant="outline-secondary" disabled={currentBatchPage === batchPageCount} onClick={() => setBatchPage(batchPageCount)}>Last</Button>
      </div>
    </nav>
  );

  const updateCase = async (caseItem, values) => {
    const nextCase = withAnnualMaskDefaultSale({ ...caseItem, ...values }, sales);
    if (nextCase.selectedSale) values = { ...values, selectedSale: nextCase.selectedSale };
    const caseRef = doc(db, caseItem.refPath);
    await updateDoc(caseRef, {
      ...values,
      updatedAt: serverTimestamp(),
      updatedBy: getCurrentUserName(currentUser),
    });

    if (caseItem.sourceFollowupPath) {
      try {
        const sourceFollowupRef = doc(db, caseItem.sourceFollowupPath);
        const sourceFollowupSnapshot = await getDoc(sourceFollowupRef);
        const sourceData = sourceFollowupSnapshot.exists()
          ? sourceFollowupSnapshot.data()
          : {};
        const sourceLink =
          sourceData.extendedProps && sourceData.extendedProps.annualMaskPurchase;
        const isCurrentLink =
          sourceLink &&
          (safeText(sourceLink.casePath) === safeText(caseItem.refPath) ||
            (safeText(sourceLink.batchId) === safeText(caseItem.batchId) &&
              safeText(sourceLink.caseId) === safeText(caseItem.id)));

        if (isCurrentLink) {
          await updateDoc(sourceFollowupRef, {
            "extendedProps.annualMaskPurchase.status":
              values.status || caseItem.status || ANNUAL_MASK_STATUS.DRAFT,
            "extendedProps.annualMaskPurchase.updatedAt": serverTimestamp(),
          });
        }
      } catch (error) {
        // The linked case remains authoritative. Do not block the operational
        // workflow if a historical follow-up event was removed.
        console.warn("Unable to sync Annual Mask status to CPAP follow-up", error);
      }
    }
    await loadData();
  };

  const sendDoctorEmail = async (caseItem) => {
    if (!canManage) {
      notifyWarning("You do not have permission to send this doctor email.");
      return;
    }
    if (!caseItem.doctorEmail) {
      notifyWarning("The selected doctor has no email address.");
      return;
    }
    if (!caseItem.usageReport) {
      notifyWarning("Upload a CPAP Usage Report before sending email.");
      return;
    }

    setSaving(true);
    let generatedCertificateRef = null;
    let doctorMailQueued = false;
    let certificateSaveStarted = false;
    try {
      const sleepStudyReport = await resolveCaseSleepStudyReport(caseItem);
      if (!sleepStudyReport) {
        notifyWarning(
          "Upload a Sleep Study Report for this patient before sending email.",
        );
        return;
      }
      const certificateBlob = await generateCaseMedicalCertificate(caseItem);
      const certificateFileName = getAnnualMaskMedicalCertFileName(
        caseItem.patient || caseItem,
      );
      const certificatePath = `annual-mask-purchases/${caseItem.hn}/${caseItem.batchId}/${caseItem.id}/generated-medical-certificate/${safeStorageName(certificateFileName)}`;
      generatedCertificateRef = ref(storage, certificatePath);
      await uploadBytes(generatedCertificateRef, certificateBlob, {
        contentType:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const certificateUrl = await getDownloadURL(generatedCertificateRef);
      const [usageAttachment, sleepStudyAttachment] = await Promise.all([
        getCaseFileMailAttachment(
          caseItem.usageReport,
          "CPAP Usage Report",
          "CPAP-Usage-Report.pdf",
        ),
        getCaseFileMailAttachment(
          sleepStudyReport,
          "Sleep Study Report",
          "Sleep-Study-Report.pdf",
        ),
      ]);
      const casePatient = caseItem.patient || caseItem;
      const casePatientName = patientName(casePatient) || caseItem.hn;

      certificateSaveStarted = true;
      await updateCase(caseItem, {
        generatedMedicalCertificate: {
          fileName: certificateFileName,
          storagePath: certificatePath,
          url: certificateUrl,
          contentType:
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          generatedAt: new Date().toISOString(),
          generatedBy: getCurrentUserName(currentUser),
        },
        sleepStudyReport,
      });

      await queueAnnualMaskEmail({
        caseItem,
        message: {
          subject: `Annual 1 Year Mask Purchase - ${casePatientName} (${caseItem.hn})`,
          html: `
            <h3>Annual 1 Year Mask Purchase</h3>
            <p><strong>Patient:</strong> ${casePatientName}</p>
            <p><strong>HN:</strong> ${caseItem.hn}</p>
            <p><strong>Doctor:</strong> ${caseItem.doctorName || "-"}</p>
            <p>Please review and sign the attached Annual 1 Year Mask Purchase medical certificate.</p>
            <p>Attached: the generated medical certificate, the CPAP Usage Report, and the Sleep Study Report.</p>
          `,
          attachments: [
            {
              filename: certificateFileName,
              path: certificateUrl,
              contentType:
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            },
            usageAttachment,
            sleepStudyAttachment,
          ],
        },
      });
      doctorMailQueued = true;
      await loadData();

      notifyTopRight(
        "Doctor email queued. The status will update when sending is confirmed.",
      );
    } catch (error) {
      console.error("Unable to send Annual Mask doctor email", error);
      const isReplacingExistingGeneratedCertificate =
        generatedCertificateRef &&
        caseItem.generatedMedicalCertificate &&
        caseItem.generatedMedicalCertificate.storagePath ===
          generatedCertificateRef.fullPath;
      if (
        generatedCertificateRef &&
        !doctorMailQueued &&
        !certificateSaveStarted &&
        !isReplacingExistingGeneratedCertificate
      ) {
        try {
          await deleteObject(generatedCertificateRef);
        } catch (cleanupError) {
          console.warn("Unable to clean up generated medical certificate", cleanupError);
        }
      }
      notifyError(error.message || "Cannot send doctor email.");
    } finally {
      setSaving(false);
    }
  };

  const downloadDoctorPackage = async (caseItem) => {
    if (!canManage) {
      notifyWarning("You do not have permission to download this package.");
      return;
    }
    if (!caseItem.usageReport) {
      notifyWarning("Upload a CPAP Usage Report before downloading the ZIP.");
      return;
    }

    setSaving(true);
    try {
      const sleepStudyReport = await resolveCaseSleepStudyReport(caseItem);
      if (!sleepStudyReport) {
        notifyWarning(
          "Upload a Sleep Study Report for this patient before downloading the ZIP.",
        );
        return;
      }

      const certificateBlob = await generateCaseMedicalCertificate(caseItem);
      const certificateFileName = getAnnualMaskMedicalCertFileName(
        caseItem.patient || caseItem,
      );
      const [usageReportBlob, sleepStudyReportBlob] = await Promise.all([
        readCaseFileBlob(caseItem.usageReport, "CPAP Usage Report"),
        readCaseFileBlob(sleepStudyReport, "Sleep Study Report"),
      ]);

      await downloadAnnualMaskDoctorPackage({
        caseItem,
        certificateBlob,
        certificateFileName,
        usageReportBlob,
        usageReportFileName:
          caseItem.usageReport.fileName || "CPAP-Usage-Report.pdf",
        sleepStudyReportBlob,
        sleepStudyReportFileName:
          sleepStudyReport.fileName || "Sleep-Study-Report.pdf",
      });
      notifyTopRight("Annual Mask Purchase doctor package downloaded.");
    } catch (error) {
      console.error("Unable to download Annual Mask doctor package", error);
      notifyError(error.message || "Cannot download the doctor package.");
    } finally {
      setSaving(false);
    }
  };

  const skipDoctorStep = async (caseItem) => {
    if (!canAdminister || saving || !canSkipAnnualMaskDoctorStep(caseItem)) return;
    setSaving(true);
    try {
      await updateCase(caseItem, { status: ANNUAL_MASK_STATUS.DOCTOR_STEP_SKIPPED });
      notifyTopRight("Certificate generation and doctor email skipped. Upload the signed certificate when ready.");
    } catch (error) {
      notifyError(error.message || "Cannot skip the doctor step.");
    } finally {
      setSaving(false);
    }
  };

  const skipSignedCertificateUpload = async (caseItem) => {
    if (!canAdminister || !caseItem || saving || !canSkipAnnualMaskSignedCertificate(caseItem)) return;
    setSaving(true);
    try {
      const confirmed = await swal({
        title: "Skip signed certificate upload?",
        text: "This will skip uploading the signed medical certificate and allow you to continue to the Social Security result without a signed PDF. Confirm that you want to skip this step.",
        icon: "warning",
        buttons: ["Cancel", "Confirm skip"],
      });
      if (!confirmed) return;
      await updateCase(caseItem, {
        signedCertificateUploadSkip: {
          confirmed: true,
          skippedAt: serverTimestamp(),
          skippedBy: getCurrentUserName(currentUser),
        },
        status: ANNUAL_MASK_STATUS.SIGNED_CERTIFICATE_SKIPPED,
      });
      notifyTopRight("Signed certificate upload skipped. You can record the Social Security result.");
    } catch (error) {
      notifyError(error.message || "Cannot skip signed certificate upload.");
    } finally {
      setSaving(false);
    }
  };

  const selectSignedCertificate = (caseItem) => {
    if (!canUploadAnnualMaskCertificate(caseItem, currentUser)) {
      notifyWarning("You do not have permission to upload this case's signed medical certificate.");
      return;
    }
    if (
      getAnnualMaskSocialSecurityDecision(caseItem) ===
        ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PASSED ||
      isAnnualMaskPurchaseResultLocked(caseItem)
    ) {
      notifyWarning(
        "Change the Social Security result to Not passed before replacing the signed certificate.",
      );
      return;
    }
    pendingSignedCaseRef.current = caseItem;
    if (signedCertificateRef.current) signedCertificateRef.current.click();
  };

  const uploadSignedCertificate = async (event) => {
    const file = event.target.files && event.target.files[0];
    const caseItem = pendingSignedCaseRef.current;
    event.target.value = "";
    pendingSignedCaseRef.current = null;

    if (!file || !caseItem || saving) return;
    if (!canUploadAnnualMaskCertificate(caseItem, currentUser) || isAnnualMaskCertificateUploadLocked(caseItem)) {
      notifyWarning("You cannot upload a certificate for this case in its current state.");
      return;
    }
    if (!isPdf(file)) {
      notifyWarning("Upload the signed medical certificate as a PDF file.");
      return;
    }

    setSaving(true);
    let uploadedCertificateRef = null;
    let signedCertificateLinked = false;
    try {
      const pressureReview = await readAnnualMaskSignedPressure(file, caseItem.certificateData);
      const savedFileName = `${Date.now()}_${safeStorageName(file.name)}`;
      const storagePath = `annual-mask-purchases/${caseItem.hn}/${caseItem.batchId}/${caseItem.id}/signed-medical-certificate/${savedFileName}`;
      if (activeScope.current !== scopeKey) throw new Error("Account changed. Open the case again before uploading.");
      uploadedCertificateRef = ref(storage, storagePath);
      await uploadBytes(uploadedCertificateRef, file, {
        contentType: file.type || "application/pdf",
      });
      const url = await getDownloadURL(uploadedCertificateRef);

      if (activeScope.current !== scopeKey) throw new Error("Account changed. Open the case again before uploading.");
      const previousCertificate = await saveAnnualMaskSignedCertificate({
        database: db,
        caseItem,
        currentUser,
        certificate: {
          pressureReview,
          fileName: file.name,
          savedFileName,
          storagePath,
          url,
          contentType: file.type || "application/pdf",
          uploadedAt: new Date().toISOString(),
          uploadedBy: getCurrentUserName(currentUser),
        },
      });
      signedCertificateLinked = true;
      if (previousCertificate && previousCertificate.storagePath && previousCertificate.storagePath !== storagePath) {
        try {
          await deleteObject(ref(storage, previousCertificate.storagePath));
        } catch (cleanupError) {
          console.warn("Unable to remove replaced signed medical certificate", cleanupError);
        }
      }
      await loadData();
      notifyTopRight("Signed medical certificate uploaded.");
    } catch (error) {
      console.error("Unable to upload signed medical certificate", error);
      if (uploadedCertificateRef && !signedCertificateLinked) {
        try {
          await deleteObject(uploadedCertificateRef);
        } catch (cleanupError) {
          console.warn("Unable to clean up signed medical certificate", cleanupError);
        }
      }
      notifyError(error.message || "Cannot upload signed medical certificate.");
    } finally {
      setSaving(false);
    }
  };

  const recordSocialSecurityDecision = async (caseItem, decision) => {
    if (!canAdminister) {
      notifyWarning("Only an administrator can record the Social Security result.");
      return;
    }
    if (!isAnnualMaskSignedCertificateStepComplete(caseItem)) {
      notifyWarning("Upload the signed medical certificate or confirm skipping this step first.");
      return;
    }
    if (isAnnualMaskPurchaseResultLocked(caseItem)) {
      notifyWarning(
        "The Social Security result cannot change after the case is ready to purchase.",
      );
      return;
    }

    const passed =
      decision === ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PASSED;
    const actor = getCurrentUserName(currentUser);
    setSaving(true);
    try {
      await updateCase(caseItem, {
        socialSecurityDecision: decision,
        socialSecurityDecisionAt: serverTimestamp(),
        socialSecurityDecisionBy: actor,
        socialSecurityApproved: passed,
        socialSecurityApprovedAt: passed ? serverTimestamp() : null,
        socialSecurityApprovedBy: passed ? actor : "",
        status: passed
          ? ANNUAL_MASK_STATUS.SOCIAL_SECURITY_APPROVED
          : ANNUAL_MASK_STATUS.SOCIAL_SECURITY_NOT_PASSED,
      });
      notifyTopRight(
        passed
          ? "Annual Mask Purchase passed by Social Security."
          : "Annual Mask Purchase marked as not passed by Social Security.",
      );
    } catch (error) {
      console.error("Unable to record Social Security result", error);
      notifyError("Cannot record the Social Security result.");
    } finally {
      setSaving(false);
    }
  };

  const updateSelectedMask = async (caseItem, accessoryId) => {
    if (
      [
        ANNUAL_MASK_STATUS.ORDER_CREATED,
        ANNUAL_MASK_STATUS.ORDER_COMPLETED,
      ].includes(caseItem.status) ||
      Boolean(caseItem.saleOrderId)
    ) {
      notifyWarning("The mask cannot change after the Sale Order is created.");
      return;
    }
    if (
      getAnnualMaskSocialSecurityDecision(caseItem) !==
      ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PASSED
    ) {
      notifyWarning("Mark the case as passed by Social Security first.");
      return;
    }
    const mask = masks.find((item) => item.id === accessoryId);
    if (!mask) {
      notifyWarning("Select a valid mask inventory item.");
      return;
    }
    if (Number(mask.stockQuantity) <= 0) {
      notifyWarning("This mask is out of stock.");
      return;
    }

    setSaving(true);
    try {
      await updateCase(caseItem, {
        selectedMask: {
          accessoryId: mask.id,
          itemCode: mask.itemCode || "",
          itemName: mask.itemName || "",
          brand: mask.brand || "",
          size: mask.size || "",
          unitPrice: Number(mask.unitPrice) || 0,
          selectedAt: new Date().toISOString(),
          selectedBy: getCurrentUserName(currentUser),
        },
      });
      notifyTopRight("Mask type selected.");
    } catch (error) {
      console.error("Unable to select annual mask", error);
      notifyError("Cannot select mask type.");
    } finally {
      setSaving(false);
    }
  };

  const updateSelectedSale = async (caseItem, saleId) => {
    if (!canAdminister) {
      notifyWarning("Only an administrator can select the Sales Representative.");
      return;
    }
    if (
      [
        ANNUAL_MASK_STATUS.ORDER_CREATED,
        ANNUAL_MASK_STATUS.ORDER_COMPLETED,
      ].includes(caseItem.status) ||
      Boolean(caseItem.saleOrderId)
    ) {
      notifyWarning(
        "The Sales Representative cannot change after the Sale Order is created.",
      );
      return;
    }
    const sale = sales.find((item) => item.id === saleId);
    if (!sale) {
      notifyWarning("Select an available Sales Representative.");
      return;
    }

    setSaving(true);
    try {
      await updateCase(caseItem, {
        selectedSale: {
          saleId: sale.id,
          name: safeText(sale.name),
          selectedAt: new Date().toISOString(),
          selectedBy: getCurrentUserName(currentUser),
        },
      });
      notifyTopRight("Sales Representative selected.");
    } catch (error) {
      console.error("Unable to select Annual Mask Sales Representative", error);
      notifyError("Cannot select the Sales Representative.");
    } finally {
      setSaving(false);
    }
  };

  const saveMaskReceiptDate = async (caseItem, date) => {
    if (!canAdminister || saving || !caseItem || caseItem.saleOrderId ||
      [ANNUAL_MASK_STATUS.ORDER_CREATED, ANNUAL_MASK_STATUS.ORDER_COMPLETED].includes(caseItem.status) ||
      !isAnnualMaskReceiptDateValid(date)) return;
    setSaving(true);
    try {
      await updateCase(caseItem, { maskReceiptDate: date });
      notifyTopRight("Expected mask receipt date saved.");
    } catch (error) {
      notifyError("Cannot save the expected mask receipt date.");
    } finally {
      setSaving(false);
    }
  };

  const createSaleOrder = async (caseItem, options = {}) => {
    const autoFromReady = Boolean(options.autoFromReady);
    if (!autoFromReady && !hasAnnualMaskAccess && !isSuperAdmin) {
      notifyWarning("Your email does not have Annual Mask Access.");
      return;
    }
    if (
      !autoFromReady &&
      caseItem.status !== ANNUAL_MASK_STATUS.READY_TO_PURCHASE
    ) {
      notifyWarning("The case must be Ready to Purchase before creating an order.");
      return;
    }

    if (!isAnnualMaskReceiptDateValid(caseItem.maskReceiptDate) && !caseItem.saleOrderId) {
      notifyWarning("Save the expected mask receipt date before marking ready.");
      return;
    }
    const selectedMask = caseItem.selectedMask || {};
    const currentMask = masks.find((item) => item.id === selectedMask.accessoryId);
    if (!currentMask) {
      notifyWarning("The selected mask is no longer available in inventory.");
      return;
    }
    const selectedSaleId = getAnnualMaskSelectedSaleId(caseItem);
    const selectedSaleName = getAnnualMaskSelectedSaleName(caseItem);
    const selectedSale = sales.find((sale) => sale.id === selectedSaleId);
    if (!selectedSaleName || !selectedSale) {
      notifyWarning("Select an available Sales Representative before marking ready.");
      return;
    }

    const confirmed = await swal({
      title: autoFromReady
        ? "Ready and create Sale Order?"
        : "Create Sale Order?",
      text: `Reserve 1 x ${
        currentMask.itemName || selectedMask.itemName
      } for ${getAnnualMaskOrderPatientLabel(
        caseItem,
      )} and assign the order to ${selectedSaleName}.`,
      icon: "warning",
      buttons: true,
    });
    if (!confirmed) return;

    setSaving(true);
    try {
      const orderResult = await createAnnualMaskSaleOrderWithStock({
        database: db,
        caseItem,
        mask: currentMask,
        currentUser,
      });
      const saleOrderStatus =
        safeText(orderResult.orderData && orderResult.orderData.status) ||
        "เตรียมเครื่อง";

      await updateCase(caseItem, {
        saleOrderId: orderResult.orderRef.id,
        saleOrderReceiptNo: orderResult.receiptNo,
        saleOrderStatus,
        saleOrderCreatedAt:
          (orderResult.orderData && orderResult.orderData.createdAt) ||
          serverTimestamp(),
        saleOrderCreatedBy:
          safeText(
            orderResult.orderData && orderResult.orderData.createdBy,
          ) || getCurrentUserName(currentUser),
        status: getAnnualMaskCaseStatusForSaleOrder(saleOrderStatus),
      });
      notifyTopRight(
        orderResult.alreadyExisted
          ? `Existing Sale Order ${orderResult.receiptNo} linked to this case.`
          : `Annual Mask Purchase is ready. Sale Order ${orderResult.receiptNo} was created automatically.`,
      );
    } catch (error) {
      console.error("Unable to create Annual Mask sale order", error);
      notifyError(error.message || "Cannot create Sale Order.");
    } finally {
      setSaving(false);
    }
  };

  const markReadyToPurchase = async (caseItem) => {
    if (!canAdminister) {
      notifyWarning("Only an administrator can approve an Annual Mask Purchase.");
      return;
    }
    if (
      getAnnualMaskSocialSecurityDecision(caseItem) !==
      ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PASSED
    ) {
      notifyWarning("Mark the case as passed by Social Security first.");
      return;
    }
    if (!caseItem.selectedMask || !caseItem.selectedMask.accessoryId) {
      notifyWarning("Select the approved mask type before marking ready.");
      return;
    }
    if (!getAnnualMaskSelectedSaleName(caseItem)) {
      notifyWarning("Select a Sales Representative before marking ready.");
      return;
    }

    await createSaleOrder(caseItem, { autoFromReady: true });
  };

  const deleteBatch = async (batch) => {
    if (physicianOnly || saleNodeOnly) return;
    if (!isSuperAdmin) {
      notifyWarning("Only Super Admin can delete an Annual Mask batch.");
      return;
    }
    const confirmed = await swal({
      title: "Delete this Annual Mask batch?",
      text: `This will remove ${batch.cases.length} case record(s) and Annual Mask workflow files. Original CPAP Usage Reports from Therapy Data are kept. Existing Sale Orders remain but are detached from the deleted batch.`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!confirmed) return;

    setSaving(true);
    try {
      const filePaths = batch.cases.reduce((paths, caseItem) => {
        [
          caseItem.usageReport,
          caseItem.generatedMedicalCertificate,
          caseItem.signedMedicalCertificate,
        ].forEach((fileData) => {
          // A case can reference a CPAP Usage Report already stored under the
          // patient follow-up files. Do not delete that original Therapy file
          // when this Annual Mask batch is removed.
          if (
            fileData &&
            fileData.storagePath &&
            fileData.annualMaskOwned !== false
          ) {
            paths.push(fileData.storagePath);
          }
        });
        return paths;
      }, []);

      await Promise.all(
        filePaths.map(async (storagePath) => {
          try {
            await deleteObject(ref(storage, storagePath));
          } catch (error) {
            console.warn("Unable to remove Annual Mask storage file", error);
          }
        }),
      );
      await Promise.all(
        batch.cases
          .filter((caseItem) => caseItem.saleOrderId)
          .map(async (caseItem) => {
            try {
              await updateDoc(doc(db, "saleOrders", caseItem.saleOrderId), {
                annualMaskCasePath: "",
                annualMaskCaseDeleted: true,
                annualMaskCaseDeletedAt: serverTimestamp(),
                annualMaskCaseDeletedBy: getCurrentUserName(currentUser),
              });
            } catch (error) {
              console.warn(
                "Unable to detach deleted Annual Mask batch from its Sale Order",
                error,
              );
            }
          }),
      );
      await Promise.all(
        batch.cases.map((caseItem) => deleteDoc(doc(db, caseItem.refPath))),
      );
      await deleteDoc(doc(db, ANNUAL_MASK_BATCHES_COLLECTION, batch.id));
      notifyTopRight("Annual Mask batch deleted.");
      await loadData();
    } catch (error) {
      console.error("Unable to delete Annual Mask batch", error);
      notifyError("Cannot delete Annual Mask batch.");
    } finally {
      setSaving(false);
    }
  };

  const openFile = async (fileData) => {
    try {
      const url = await getCaseFileUrl(fileData);
      if (!url) {
        notifyWarning("This file is unavailable.");
        return;
      }
      setFilePreview({
        title: fileData.fileName || "File",
        url,
        type: getFileType(fileData.fileName || fileData.storagePath),
      });
    } catch (error) {
      console.error("Unable to open Annual Mask file", error);
      notifyError("Cannot open file.");
    }
  };

  const getSelectedMaskInventory = (caseItem) =>
    masks.find(
      (mask) =>
        mask.id ===
        safeText(caseItem.selectedMask && caseItem.selectedMask.accessoryId),
    );

  const renderFileButton = (label, fileData, variant = "outline-primary") =>
    fileData ? (
      <Button
        size="sm"
        variant={variant}
        className="mr-1 mb-1"
        onClick={() => openFile(fileData)}
      >
        <i className="fa fa-file-o mr-1" />
        {label}
      </Button>
    ) : (
      <span className="text-muted">-</span>
    );

  const renderBatchCases = (batch) =>
    batch.cases.map((storedCase) => {
      const caseItem = withAnnualMaskDefaultSale(storedCase, sales);
      if (physicianOnly) return (
        <AnnualMaskPhysicianCase key={caseItem.refPath} caseItem={caseItem} currentUser={currentUser} saving={saving} onUpload={selectSignedCertificate} renderFileButton={renderFileButton} />
      );
      const statusConfig = getAnnualMaskStatusConfig(caseItem.status);
      const selectedInventory = getSelectedMaskInventory(caseItem);
      const purchaseIsReady = [
        ANNUAL_MASK_STATUS.READY_TO_PURCHASE,
        ANNUAL_MASK_STATUS.ORDER_CREATED,
        ANNUAL_MASK_STATUS.ORDER_COMPLETED,
      ].includes(caseItem.status);
      const saleOrderIsLocked =
        [
          ANNUAL_MASK_STATUS.ORDER_CREATED,
          ANNUAL_MASK_STATUS.ORDER_COMPLETED,
        ].includes(caseItem.status) || Boolean(caseItem.saleOrderId);
      const socialSecurityDecision =
        getAnnualMaskSocialSecurityDecision(caseItem);
      const socialSecurityPassed =
        socialSecurityDecision ===
        ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PASSED;
      const socialSecurityNotPassed =
        socialSecurityDecision ===
        ANNUAL_MASK_SOCIAL_SECURITY_DECISION.NOT_PASSED;
      const selectedSaleId = getAnnualMaskSelectedSaleId(caseItem);
      const selectedSaleName = getAnnualMaskSelectedSaleName(caseItem);
      const selectedSaleIsAvailable = sales.some(
        (sale) => sale.id === selectedSaleId,
      );
      const canCreateOrder =
        caseItem.status === ANNUAL_MASK_STATUS.READY_TO_PURCHASE &&
        !canAdminister &&
        (isSuperAdmin || hasAnnualMaskAccess);

      return (
        <details className="annual-mask-case" key={caseItem.id}>
          <summary className="annual-mask-case-summary">
            <div className="annual-mask-case-identity">
              <strong>{patientName(caseItem.patient || caseItem) || caseItem.hn}</strong>
              <span className="small text-muted">
                {caseItem.hn} · {caseItem.nodeRef || (caseItem.patient && caseItem.patient.nodeRef) || "-"}
              </span>
            </div>
            <div className="annual-mask-case-overview small">
              <span>Doctor: {caseItem.doctorName || "-"}</span>
              <span>Sales: {selectedSaleName || "Not selected"}</span>
              <AnnualMaskReceiptDateText date={caseItem.maskReceiptDate} />
              <span>Mask: {(caseItem.selectedMask && caseItem.selectedMask.itemName) || "Not selected"}</span>
              {caseItem.saleOrderReceiptNo && <span>Order: {caseItem.saleOrderReceiptNo}</span>}
            </div>
            <div className="annual-mask-case-status">
              <Badge variant={statusConfig.badge}>{statusConfig.label}</Badge>
              <span className="annual-mask-case-toggle small">
                <span className="annual-mask-show-details">Show details &amp; actions</span>
                <span className="annual-mask-hide-details">Hide details</span>
                <i className="fa fa-chevron-down ml-2" aria-hidden="true" />
              </span>
            </div>
          </summary>
          <div className="annual-mask-case-content">
            <AnnualMaskWorkflowProgress caseItem={caseItem} />
            <div className="annual-mask-case-grid">

              <section className="annual-mask-case-section">
                <h6>Patient &amp; doctor</h6>

            <Link to={`/patient-page/${caseItem.hn}`}>{caseItem.hn}</Link>
            <div className="small text-muted">
              {patientName(caseItem.patient || caseItem) || "-"}
            </div>
            {caseItem.sourceFollowupDate && (
              <div className="small text-muted">
                CPAP Follow-Up: {caseItem.sourceFollowupDate}
                {caseItem.sourceFollowupType
                  ? ` · ${caseItem.sourceFollowupType}`
                  : ""}
              </div>
            )}

            <div className="mt-3"><div className="small text-muted">Doctor</div>
            {caseItem.doctorName || "-"}
            <div className="small text-muted">{caseItem.doctorEmail || ""}</div>
          </div>
            <div className="mt-2">
            <Badge variant={statusConfig.badge}>{statusConfig.label}</Badge>
            <AnnualMaskDoctorEmailStatus caseItem={caseItem} onChange={(fields) => setBatches((rows) => rows.map((batch) => ({ ...batch, cases: (batch.cases || []).map((item) => item.refPath === caseItem.refPath ? { ...item, ...fields } : item) })))} />
          </div>
              </section>
              <section className="annual-mask-case-section">
                <h6>Documents</h6>

            {renderFileButton("Usage Report", caseItem.usageReport)}
            {caseItem.sleepStudyReport &&
              renderFileButton(
                "Sleep Study",
                caseItem.sleepStudyReport,
                "outline-info",
              )}
            {renderFileButton(
              "Generated Form",
              caseItem.generatedMedicalCertificate,
              "outline-secondary",
            )}
            {renderFileButton(
              "Review signed cert",
              caseItem.signedMedicalCertificate,
              "outline-success",
            )}
            {caseItem.signedMedicalCertificate && (
              <small
                className="d-block text-success"
                title={caseItem.signedMedicalCertificate.fileName || ""}
              >
                {caseItem.signedMedicalCertificate.fileName ||
                  "Signed medical certificate.pdf"}
              </small>
            )}

              </section>
              <section className="annual-mask-case-section">
                <h6>Social Security</h6>

            {canAdminister ? (
              <>
                <small className="d-block text-muted mb-2">
                  Files are sent outside this system. Record the result only.
                </small>
                <Button
                  size="sm"
                  variant={socialSecurityPassed ? "success" : "outline-success"}
                  className="mr-1 mb-1"
                  disabled={
                    saving ||
                    !isAnnualMaskSignedCertificateStepComplete(caseItem) ||
                    purchaseIsReady ||
                    Boolean(caseItem.saleOrderId)
                  }
                  onClick={() =>
                    recordSocialSecurityDecision(
                      caseItem,
                      ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PASSED,
                    )
                  }
                >
                  <i className="fa fa-check-circle mr-1" />
                  Passed
                </Button>
                <Button
                  size="sm"
                  variant={socialSecurityNotPassed ? "danger" : "outline-danger"}
                  className="mb-1"
                  disabled={
                    saving ||
                    !isAnnualMaskSignedCertificateStepComplete(caseItem) ||
                    purchaseIsReady ||
                    Boolean(caseItem.saleOrderId)
                  }
                  onClick={() =>
                    recordSocialSecurityDecision(
                      caseItem,
                      ANNUAL_MASK_SOCIAL_SECURITY_DECISION.NOT_PASSED,
                    )
                  }
                >
                  <i className="fa fa-times-circle mr-1" />
                  Not passed
                </Button>
                {socialSecurityDecision ===
                  ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PENDING && (
                  <small className="d-block text-muted mt-1">
                    Waiting for result
                  </small>
                )}
                {socialSecurityDecision !==
                  ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PENDING && (
                  <small
                    className={`d-block mt-1 ${
                      socialSecurityPassed ? "text-success" : "text-danger"
                    }`}
                  >
                    Updated {formatDateTime(
                      caseItem.socialSecurityDecisionAt ||
                        caseItem.socialSecurityApprovedAt,
                    )}
                  </small>
                )}
              </>
            ) : (
              <Badge
                variant={
                  socialSecurityPassed
                    ? "success"
                    : socialSecurityNotPassed
                    ? "danger"
                    : "secondary"
                }
              >
                {socialSecurityPassed
                  ? "Passed"
                  : socialSecurityNotPassed
                  ? "Not passed"
                  : "Waiting"}
              </Badge>
            )}

              </section>
              <section className="annual-mask-case-section">
                <h6>Purchase details</h6>

                <label className="small text-muted">Sales Representative</label>
            {canAdminister ? (
              <>
                <Form.Control
                  as="select"
                  size="sm"
                  value={selectedSaleId}
                  disabled={
                    !socialSecurityPassed || saving ||
                    saleOrderIsLocked
                  }
                  onChange={(event) =>
                    updateSelectedSale(caseItem, event.target.value)
                  }
                >
                  <option value="">Select Sales Representative</option>
                  {selectedSaleId && !selectedSaleIsAvailable && (
                    <option value={selectedSaleId} disabled>
                      {selectedSaleName || selectedSaleId} — Not available
                    </option>
                  )}
                  {sales.map((sale) => (
                    <option key={sale.id} value={sale.id}>
                      {sale.name}
                    </option>
                  ))}
                </Form.Control>
                <small className="d-block text-muted mt-1">
                  Required before Ready to Purchase
                </small>
              </>
            ) : (
              selectedSaleName || <span className="text-muted">Not selected</span>
            )}

                {canAdminister && <AnnualMaskReceiptDate date={caseItem.maskReceiptDate} disabled={saving || saleOrderIsLocked} onSave={(date) => saveMaskReceiptDate(caseItem, date)} />}
                <label className="small text-muted mt-3">Mask stock / lots</label>
            {canAdminister ? (
              <>
                <Form.Control
                  as="select"
                  size="sm"
                  value={safeText(caseItem.selectedMask && caseItem.selectedMask.accessoryId)}
                  disabled={
                    saving ||
                    !socialSecurityPassed ||
                    saleOrderIsLocked
                  }
                  onChange={(event) => updateSelectedMask(caseItem, event.target.value)}
                >
                  <option value="">Select mask type</option>
                  {masks.map((mask) => (
                    <option
                      key={mask.id}
                      value={mask.id}
                      disabled={Number(mask.stockQuantity) <= 0}
                    >
                      {mask.itemName} {mask.size ? `(${mask.size})` : ""} — Stock: {mask.stockQuantity}
                    </option>
                  ))}
                </Form.Control>
                {selectedInventory && (
                  <small className="d-block text-muted mt-1">
                    Stock left: {selectedInventory.stockQuantity}. Lots: {formatLots(selectedInventory.lots)}
                  </small>
                )}
              </>
            ) : (
              <>
                <span>
                  {(caseItem.selectedMask && caseItem.selectedMask.itemName) ||
                    "-"}
                </span>
                {selectedInventory && (
                  <small className="d-block text-muted mt-1">
                    Stock left: {selectedInventory.stockQuantity}. Lots: {formatLots(selectedInventory.lots)}
                  </small>
                )}
              </>
            )}

              </section>
            </div>
            <section className="annual-mask-case-actions">
              <h6>Workflow actions</h6>
              <div className="annual-mask-action-list">
                <AnnualMaskTestEmailButton caseItem={caseItem} disabled={saving} />

            {canManage && (
              <Fragment>
                <Button
                  size="sm"
                  variant="outline-info"
                  className="mr-1 mb-1"
                  disabled={saving || !canGenerateAnnualMaskMedicalCert(caseItem)}
                  onClick={() => sendDoctorEmail(caseItem)}
                  title="Generate and email the Annual 1 Year Mask Purchase form with CPAP Usage Report and Sleep Study Report"
                >
                  <i className="fa fa-send mr-1" />
                  {caseItem.doctorEmailSent
                    ? "Resend doctor email"
                    : "Send doctor email"}
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="mr-1 mb-1"
                  disabled={saving || !caseItem.usageReport || !canGenerateAnnualMaskMedicalCert(caseItem)}
                  onClick={() => downloadDoctorPackage(caseItem)}
                  title="Download the medical certificate, CPAP Usage Report, and Sleep Study Report as one ZIP"
                >
                  <i className="fa fa-download mr-1" />
                  Download ZIP
                </Button>
              </Fragment>
            )}
            {canAdminister && (
              <>
                <Button
                    size="sm"
                    variant="outline-secondary"
                    className="mr-1 mb-1"
                    disabled={saving || !canSkipAnnualMaskDoctorStep(caseItem)}
                            title={getAnnualMaskDoctorSkipUnavailableReason(caseItem)}
                    onClick={() => skipDoctorStep(caseItem)}
                  >
                    Skip generation &amp; doctor email
                  </Button>
                        {!canSkipAnnualMaskDoctorStep(caseItem) && (
                          <small className="d-block text-muted mb-1">{getAnnualMaskDoctorSkipUnavailableReason(caseItem)}</small>
                        )}
                <Button
                  size="sm"
                  variant="outline-primary"
                  className="mr-1 mb-1"
                  disabled={
                    saving ||
                    socialSecurityPassed ||
                    purchaseIsReady ||
                    Boolean(caseItem.saleOrderId)
                  }
                  onClick={() => selectSignedCertificate(caseItem)}
                  title="Upload a signed PDF directly, including your node's own template"
                >
                  {caseItem.signedMedicalCertificate
                    ? "Replace signed cert"
                    : "Upload signed cert"}
                </Button>
                {canSkipAnnualMaskSignedCertificate(caseItem) && (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="ml-2 mb-1"
                    disabled={saving}
                    onClick={() => skipSignedCertificateUpload(caseItem)}
                  >
                    Skip signed certificate upload
                  </Button>
                )}
                {caseItem.signedCertificateUploadSkip && caseItem.signedCertificateUploadSkip.confirmed && !caseItem.signedMedicalCertificate && (
                  <small className="d-block text-muted mt-1">Signed certificate upload skipped by confirmation.</small>
                )}

                {!canGenerateAnnualMaskMedicalCert(caseItem) && (
                  <small className="d-block text-muted mb-1">
                    Use your node's own template and upload the signed PDF.
                  </small>
                )}
              </>
            )}
            {canAdminister && (
              <Button
                size="sm"
                variant="success"
                className="mr-1 mb-1"
                disabled={
                  saving ||
                  !socialSecurityPassed ||
                  !caseItem.selectedMask ||
                  !caseItem.selectedMask.accessoryId ||
                  !selectedSaleName ||
                  !selectedSaleIsAvailable ||
                  !isAnnualMaskReceiptDateValid(caseItem.maskReceiptDate) ||
                  saleOrderIsLocked
                }
                onClick={() => markReadyToPurchase(caseItem)}
              >
                {caseItem.status === ANNUAL_MASK_STATUS.READY_TO_PURCHASE
                  ? "Retry & create order"
                  : "Ready & create order"}
              </Button>
            )}
            <AnnualMaskCompleteButton caseItem={caseItem} currentUser={currentUser} disabled={saving} onComplete={loadData} />
            {caseItem.saleOrderReceiptNo ? (
              <Link
                to="/sale-order-list"
                className="btn btn-outline-secondary btn-sm mb-1"
                title={`Sale order ${caseItem.saleOrderReceiptNo}`}
              >
                Order: {caseItem.saleOrderReceiptNo}
              </Link>
            ) : canCreateOrder ? (
              <Button
                size="sm"
                variant="primary"
                className="mb-1"
                disabled={saving || !selectedSaleName || !selectedSaleIsAvailable || !isAnnualMaskReceiptDateValid(caseItem.maskReceiptDate)}
                onClick={() => createSaleOrder(caseItem)}
                title="Recovery action for a Ready case that does not yet have an order"
              >
                Retry Sale Order
              </Button>
            ) : null}

              </div>
            </section>
          </div>
        </details>
      );
    });

  return (
    <Fragment>
      <Toast />
      <PageTitle
        activeMenu={physicianOnly ? "Annual Mask — Signed Certificates" : "Annual Mask Purchase Batch Review"}
        motherMenu="CPAP Follow-Up"
      />

      <input
        ref={signedCertificateRef}
        type="file"
        accept="application/pdf,.pdf"
        className="d-none"
        onChange={uploadSignedCertificate}
      />

      <Modal
        show={Boolean(filePreview)}
        onHide={() => setFilePreview(null)}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>{filePreview && filePreview.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ height: "80vh" }}>
          {filePreview && filePreview.type === "image" ? (
            <img
              src={filePreview.url}
              alt={filePreview.title}
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          ) : filePreview ? (
            <iframe
              title="Annual Mask Purchase file preview"
              src={`${filePreview.url}#toolbar=0`}
              width="100%"
              height="100%"
              style={{ border: 0 }}
            />
          ) : null}
        </Modal.Body>
      </Modal>

      <Card className="mb-4 annual-mask-toolbar">
        <Card.Body className="d-flex flex-wrap align-items-end">
          <div className="w-100 mb-3">
            <h6 className="mb-1">{physicianOnly ? "My cases — Signed medical certificates" : "Annual Mask Purchase Batch Review"}</h6>
            <small className="text-muted">
              {saleNodeOnly && <span className="d-block">Showing only cases from your assigned nodes.</span>}
              {physicianOnly ? "Showing cases awaiting your signed certificate by default. Select Uploaded or All certificates to review completed uploads." : "Open a case to review its documents and complete the purchase steps. Create new batches from the Annual Mask Purchase tab in CPAP Follow-Up."}
            </small>
          </div>
          <div className="annual-mask-search mr-2 mb-2">
            <label className="mb-1">{physicianOnly ? "Search patient, HN, or batch" : "Search batch, HN, patient, doctor, sales, or order"}</label>
            <input
              className="form-control"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search Annual Mask Purchase"
            />
          </div>
          <div className="annual-mask-filter mr-2 mb-2">
            <label className="mb-1">Status</label>
            <select
              className="form-control"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="All Status">{physicianOnly ? "All certificates" : "All Status"}</option>
              {(physicianOnly ? ["Awaiting upload", "Uploaded", "Locked"] : Object.values(ANNUAL_MASK_STATUS))
                .filter(
                  (status) =>
                    ![
                      ANNUAL_MASK_STATUS.SOCIAL_SECURITY_FILE_CHECKED,
                      ANNUAL_MASK_STATUS.SOCIAL_SECURITY_SUBMITTED,
                    ].includes(status),
                )
                .map((status) => (
                  <option key={status}>{status}</option>
                ))}
            </select>
          </div>
          {isSuperAdmin && <>
            <div className="annual-mask-filter mr-2 mb-2">
              <label className="mb-1" htmlFor="annual-mask-node-filter">Node</label>
              <select
                id="annual-mask-node-filter"
                className="form-control"
                value={nodeFilter}
                onChange={(event) => { setNodeFilter(event.target.value); setCustomerFilter(""); }}
              >
                <option value="">All Nodes</option>
                {nodeOptions.map((node) => <option key={node} value={node}>{node}</option>)}
              </select>
            </div>
            <div className="annual-mask-filter mr-2 mb-2">
              <label className="mb-1" htmlFor="annual-mask-customer-filter">Customer</label>
              <select
                id="annual-mask-customer-filter"
                className="form-control"
                value={customerFilter}
                onChange={(event) => setCustomerFilter(event.target.value)}
              >
                <option value="">All Customers</option>
                {customerOptions.map((customer) => <option key={customer} value={customer}>{customer}</option>)}
              </select>
            </div>
          </>}
          <Button
            variant="outline-secondary"
            className="mr-2 mb-2"
            onClick={() => { setSearch(""); setStatusFilter(defaultStatusFilter); setNodeFilter(""); setCustomerFilter(""); }}
            disabled={!search && statusFilter === defaultStatusFilter && !nodeFilter && !customerFilter}
          >
            Clear filters
          </Button>
          <Button
            variant="outline-secondary"
            className="mr-2 mb-2"
            onClick={loadData}
            disabled={loading || saving}
          >
            <i className="fa fa-refresh mr-1" /> Refresh
          </Button>
          {!physicianOnly && <Link to="/cpap-followup-calendar" className="btn btn-primary mb-2">
            Open CPAP Follow-Up
          </Link>}
        </Card.Body>
      </Card>

      {!loading && renderBatchPagination()}
      {loading ? (
        <div className="p-3 text-muted">Loading Annual Mask Purchase batches…</div>
      ) : filteredBatches.length ? (
        visibleBatches.map((batch) => (
          <Card className="mb-2 annual-mask-batch" key={batch.id}>
            <Card.Header className="annual-mask-batch-header">
              <div>
                <h4 className="card-title mb-1">{batch.batchName || batch.id}</h4>
                <small className="text-muted">
                  Created by {batch.createdBy || "-"} • {formatDateTime(batch.createdAt)} • {batch.cases.length} case(s)
                </small>
              </div>
              <div className="mt-2 mt-md-0">
                {isSuperAdmin && !physicianOnly && !saleNodeOnly && (
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => deleteBatch(batch)}
                    disabled={saving || batches.some((sourceBatch) => sourceBatch.id === batch.id && sourceBatch.cases.length !== batch.cases.length)}
                    title="Clear filters to show all cases before deleting a batch."
                  >
                    Delete batch
                  </Button>
                )}
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {renderBatchCases(batch)}
            </Card.Body>
          </Card>
        ))
      ) : (
        <Card>
          <Card.Body className="text-muted">
            No Annual Mask Purchase cases match the current filters.
          </Card.Body>
        </Card>
      )}
      {!loading && batchPageCount > 1 && renderBatchPagination()}
    </Fragment>
  );
};

export default AnnualMaskPurchase;
